import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { termsApi } from '../features/terms/api.ts';
import { downloadAuthed } from '../features/meetings/api.ts';
import { type Body } from '../lib/body.ts';
import { dv } from '../i18n/dv.ts';

// Settings › Data export — pick a body + dhauru (term) and download a ZIP of every
// meeting's documents (a folder per meeting) plus an Excel workbook of all
// meeting details. 'all' spans every term. Org admins only (route-guarded).
export function DataExport(): JSX.Element {
  const [body, setBody] = useState<Body>('COUNCIL');
  const { data: terms } = useQuery({ queryKey: ['terms', body], queryFn: () => termsApi.list(body) });

  // Selected term id, or 'all'. Default to the body's active term once loaded.
  const [termId, setTermId] = useState<string>('all');
  useEffect(() => {
    if (terms && terms.length > 0) setTermId(terms.find((t) => t.isActive)?.id ?? terms[0]!.id);
    else setTermId('all');
  }, [terms]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const run = async () => {
    setBusy(true);
    setError(false);
    try {
      const term = terms?.find((t) => t.id === termId);
      const scope = term ? `dhauru-${term.number}` : 'all-terms';
      await downloadAuthed(
        `/org/export?body=${body}&termId=${encodeURIComponent(termId)}`,
        `wapmeet-${body.toLowerCase()}-${scope}.zip`,
      );
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      <BodyToggle body={body} onChange={setBody} />

      <section className="space-y-4 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <div className="space-y-1">
          <h2 className="section-heading text-lg font-bold">{dv.settings.export.title}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{dv.settings.export.intro}</p>
        </div>

        <div className="space-y-1.5">
          <label className="field-label" htmlFor="export-term">{dv.settings.export.selectTerm}</label>
          <select
            id="export-term"
            value={termId}
            onChange={(e) => setTermId(e.target.value)}
            className="select w-full sm:w-72"
          >
            {(terms ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.number} ވަނަ ދައުރު</option>
            ))}
            <option value="all">{dv.settings.export.allTerms}</option>
          </select>
        </div>

        <div className="flex items-center gap-3 border-t border-black/[0.05] pt-4">
          <button type="button" onClick={run} disabled={busy} className="btn-primary">
            <Icon name="download" size={16} />
            {busy ? dv.settings.export.preparing : dv.settings.export.download}
          </button>
          {error && <span className="text-sm text-destructive">{dv.settings.export.error}</span>}
        </div>
      </section>
    </div>
  );
}
